import axios from 'axios';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import { keepHoleIds } from './holeIdsKept';
import type { ByHoles } from './stepHolesRun';
import type { PDFDocumentLoadingTask } from 'pdfjs-dist';
import { loadPdf, renderPdfPageBand } from './pdfRender';
import { buildInteractionsPrompt, type InteractionsContext } from './hardwareInteractionsPrompt';
import { namedIn, storedId, withLetter } from './partsCatalogue';
import type { WrittenInteraction } from './openaiBlenderApi';
import type { StepBand } from './categoryBands';
import type { PartItem } from './partsRun';
import { pieceContents, pieceNames, type SubStep } from './stepsRun';
import { ensureCategories } from './catalogCategories';
import { haltGate } from './runHalt';

// PHASE 4'S HARDWARE TABLE — which pieces of hardware work together, over the whole manual.
// One call, after the analysis and over its answer; see hardwareInteractionsPrompt for what it
// asks and why it is one call and not one per step.

// ONE HOLE THE TABLE SAYS AN ENTRY MAKES: in which component part, what kind — all the way
// through, stopping inside, or across another hole (the barrel nut's, across the screw's) — by
// which face of the finished product, how many in one piece of that part, and for which
// hardware — the ENTRY's hardware only, so on one side of a mirror pair a shared part (the seat
// both rails screw into) counts that side's holes. Or 'none': a part the hardware meets with no hole (a felt pad's foot), no face and no
// count. WHAT holes there are; where on the part is Find holes' (see stepHolesPrompt), and how
// wide is the hardware's own size.
//
// THE TABLE'S ONE ANSWER ABOUT COMPONENT PARTS: an entry's `components` and `through` are read off
// its holes by the server (see _parse_hardware_interactions), so everything reading those follows it.
export type TableHole = {
  part: string;
  kind: 'through' | 'blind' | 'cross' | 'none';
  face: string;
  count: number;
  for: string;
  // ON A CROSS HOLE, THE HOLE IT MEETS, by the hardware that hole is for — a barrel nut's hole
  // crossing the screw's. Lines have no ids, and within one entry and one part each piece of
  // hardware makes its own, so the hardware names it. Null where the answer named none, or one
  // that is not a line of the same part (checked on the server), and on every other kind.
  crosses?: string | null;
  // ON A THROUGH OR BLIND HOLE, THE OTHER PART whose hole the same hardware continues into, so
  // the two must line up — the screw's through hole in the frame and its blind hole in the
  // rail. A part id; null where none (checked on the server: that part has a through or blind
  // line for the same hardware), and on every answer from before it was asked.
  aligned?: string | null;
  // THE HOLES' IDS — "H101", one per hole — as the call gave them and stored with the line. They are
  // the holes' ids everywhere after: Drilling holes places each hole under its id (see holeSlots).
  // Absent on every answer from before they were asked.
  ids?: string[];
  // WHICH STRETCH OF THE HARDWARE IS IN THESE HOLES, along the axis it goes in by: the end that goes
  // in first, the end it is pushed or turned by, neither (it passes through), or all of it. Null
  // where the call gave none of the four; absent on every answer from before it was asked.
  portion?: 'leading' | 'trailing' | 'middle' | 'whole' | null;
};

// ONE INTERACTION: the hardware parts (stored ids, in the order they come together), the
// component parts they go through, the sub-steps ("2C") where the second, third... is added,
// and how they engage, in both languages.
// WHICH OF A MIRROR PAIR AN ENTRY IS ABOUT. An entry meeting a part supplied as a left and a
// right one is asked as two, one per side, each with its own sub-steps and holes — a count over
// both rails, drawn beside one of them, was twice the holes the screws make. Null where no pair
// is met, and on every answer from before it was asked.
export type EntrySide = 'left' | 'right' | null;

export type HardwareInteraction = {
  hardware: string[];
  // HOW MANY OF EACH the entry uses — on its side, for a mirror pair. By stored id; an id with
  // no count (and every answer from before it was asked) has none, and none is shown.
  counts: Record<string, number>;
  side: EntrySide;
  // The ids in `hardware` that cover another one's hole — a wood button capping a screw. Where
  // a button goes follows from that screw's hole (see Phase 7). Empty on an answer from before
  // it was asked.
  covers: string[];
  // THE WASHERS, each on the piece it goes on — {washer id: that piece's id}, under a screw's head.
  // A washer makes no hole, and holds that much of the piece out of the wood (see insertionPrompt).
  // Empty on an answer from before it was asked.
  washers: Record<string, string>;
  components: string[];
  // THE ONES IT GOES ALL THE WAY THROUGH, out the far side — a screw through the frame into the
  // rail goes through the frame and not the rail. Empty on an answer from before it was asked.
  through: string[];
  substeps: string[];
  // The holes it makes — see TableHole. Empty on an answer from before it was asked.
  holes: TableHole[];
  descriptionEn: string;
  descriptionFr: string;
  // ITS SUB-STEPS ARE ANOTHER ANALYSIS'S: the table was read against sub-steps Phase 4 has since
  // replaced, so `substeps` names letters that now mean other gestures. Set on reading, never
  // stored — see matchingRows.
  stale?: true;
};

// A PIECE OF HARDWARE IN NO INTERACTION, and the component parts it goes into — a chipboard
// screw straight into the seat. Empty on an answer from before it was asked.
export type HardwareAlone = {
  hardware: string; count: number | null; side: EntrySide; components: string[]; substeps: string[];
  // The ones it goes all the way through — see HardwareInteraction.through.
  through: string[];
  // A COVER STRAIGHT ON THE COMPONENT — a felt pad on a foot: no hardware under it, so no hole.
  covers: boolean;
  holes: TableHole[];
  descriptionEn: string; descriptionFr: string;
};

type Snapshot = {
  busy: boolean;
  interactions: HardwareInteraction[];
  alone: HardwareAlone[];
  // Whether the call has ever answered for this manual — an empty table after a run is an
  // answer ("no two pieces of hardware meet"), an empty table before one is not.
  asked: boolean;
  reasoning: string;
  raw: string;
  error: string | null;
  startedAt: number | null;
  elapsed: number | null;
  // WHAT IT WAS BUILT FROM — see stepsBasis. Null on a table from before it was recorded, which
  // cannot be told apart from a current one and is not treated as outdated.
  basis: string | null;
};

const key = (docId: string) => `notice.hardwareInteractions.${docId}`;
type Stored = {
  interactions?: HardwareInteraction[]; alone?: HardwareAlone[]; reasoning?: string; raw?: string;
  elapsed?: number | null;
  basis?: string;
};

// EVERY FIELD LISTED, as the holes loader does: a field left out of a mapper lives in memory
// until the first reload and then quietly is not there.
// THE HOLES ALREADY PLACED, by the ids they were placed under — what a table's ids are kept to (see
// keepHoleIds).
const placedHoles = (docId: string) =>
  Object.values(readState<ByHoles>(docId, `notice.holes.byStep.${docId}`, {})).flatMap((f) => f.holes);

const load = (docId: string): Snapshot => {
  const stored = readState<Stored | null>(docId, key(docId), null);
  const read = {
    interactions: (stored?.interactions ?? []).map((one): HardwareInteraction => ({
      hardware: one.hardware ?? [],
      counts: one.counts ?? {},
      side: one.side ?? null,
      covers: one.covers ?? [],
      washers: one.washers ?? {},
      components: one.components ?? [],
      through: one.through ?? [],
      substeps: one.substeps ?? [],
      holes: one.holes ?? [],
      descriptionEn: one.descriptionEn ?? '',
      descriptionFr: one.descriptionFr ?? '',
    })),
    alone: (stored?.alone ?? []).map((one): HardwareAlone => ({
      hardware: one.hardware,
      count: one.count ?? null,
      side: one.side ?? null,
      components: one.components ?? [],
      substeps: one.substeps ?? [],
      covers: !!one.covers,
      through: one.through ?? [],
      holes: one.holes ?? [],
      descriptionEn: one.descriptionEn ?? '',
      descriptionFr: one.descriptionFr ?? '',
    })),
  };
  // A TABLE READ BEFORE ITS HOLES WERE RENUMBERED OR PLACED, named as the wood is (Louis 2026-10-29): the
  // Josette's, read on 20 Oct, called the right screw H206 where Find holes had placed it as H202.
  const kept = keepHoleIds<HardwareInteraction | HardwareAlone>([...read.interactions, ...read.alone], [], placedHoles(docId));
  return {
    busy: false,
    interactions: kept.slice(0, read.interactions.length) as HardwareInteraction[],
    alone: kept.slice(read.interactions.length) as HardwareAlone[],
    asked: !!stored,
    reasoning: stored?.reasoning ?? '',
    raw: stored?.raw ?? '',
    error: null,
    startedAt: null,
    elapsed: stored?.elapsed ?? null,
    basis: stored?.basis ?? null,
  };
};

const store = createDocEntryStore<Snapshot>((docId) => ({ snapshot: load(docId) }));

export const useHardwareInteractions = store.useSnapshot;
export const getHardwareInteractions = store.get;

// EVERY ROW THAT SAYS WHICH PARTS HARDWARE GOES INTO, the lone pieces as rows of one — what Find
// holes narrows its parts by (see drillGroupParts).
export const touchRows = (snap: { interactions: HardwareInteraction[]; alone: HardwareAlone[] }) =>
  [...snap.interactions, ...snap.alone.map((one) => ({
    hardware: [one.hardware], counts: one.count ? { [one.hardware]: one.count } : {}, side: one.side, covers: one.covers ? [one.hardware] : [],
    washers: {} as Record<string, string>, components: one.components,
    through: one.through, substeps: one.substeps, holes: one.holes,
    descriptionEn: one.descriptionEn, descriptionFr: one.descriptionFr,
  }))];

// THE PARTS THAT ARE WASHERS, as the table puts them on another piece (HardwareInteraction.washers) —
// by stored id. Not asked about on their own after it: what one changes is how far the piece it is
// on goes in (see insertionPrompt).
export const washerIds = (snap: { interactions: HardwareInteraction[] }) =>
  new Set(snap.interactions.flatMap((one) => Object.keys(one.washers)));

// THE PARTS THAT ARE COVERS — a wood button over a screw's hole, a felt pad under a foot — by stored
// id. Built with the rest of the hardware in Phase 6, and placed in its Covers placement subphase.
export const coverIds = (snap: { interactions: HardwareInteraction[]; alone: HardwareAlone[] }) =>
  new Set([...snap.interactions.flatMap((one) => one.covers),
    ...snap.alone.filter((one) => one.covers).map((one) => one.hardware)]);

// THE SUB-STEPS A TABLE IS BUILT FROM, as one string: each step's sub-steps, their letters and
// the parts each brings. Recorded with the table, and compared with the analysis on screen: the
// table's rows name sub-steps ("2C") and parts, and a re-analysis that changed either leaves
// rows describing sub-steps that no longer exist — which Find holes must not read (see
// FindHoles). The content, not a time: re-running the analysis to the same answer is not a
// change.
export function stepsBasis(steps: StepBand[], subSteps: Record<string, SubStep[]>): string {
  return steps.map((s) => `${s.num}:${(subSteps[s.category] ?? [])
    .map((sub) => `${sub.letter}=${(sub.parts ?? []).map((u) => u.id).sort().join('.')}`)
    .join(',')}`).join(';');
}

// Whether the table was built from other sub-steps than these — see stepsBasis.
export const tableOutdated = (
  snap: { basis: string | null }, steps: StepBand[], subSteps: Record<string, SubStep[]>,
) => snap.basis != null && snap.basis !== stepsBasis(steps, subSteps);

// THE ROWS AS GESTURES ARE MATCHED TO THEM: every row, each marked stale where the table was
// read against other sub-steps than these (see tableOutdated), so a row is never matched by a
// label of an analysis that is gone (see drillGroupParts). Its step numbers still hold.
export const matchingRows = (
  table: Parameters<typeof touchRows>[0] & { basis: string | null },
  steps: StepBand[], subSteps: Record<string, SubStep[]>,
): HardwareInteraction[] => {
  const rows = touchRows(table);
  return tableOutdated(table, steps, subSteps) ? rows.map((r) => ({ ...r, stale: true as const })) : rows;
};

// THE CONTEXT THE CALL IS GIVEN, built here and used by both the run and the badge — so the
// preview is byte for byte what is sent.
export function interactionsContextFor(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
): InteractionsContext {
  return {
    steps,
    subSteps,
    items,
    contents: pieceContents(steps, subSteps),
    // English: prompt copy.
    pieces: pieceNames(steps, subSteps, 'en'),
  };
}

// HOW WIDE EACH STEP PICTURE IS RENDERED. Narrower than a single step's call (1100): this one
// carries every step at once, and a line drawing of a screw going into a nut reads at 900.
const WIDTH = 900;
const MAX_IMAGES = 24;

// Run the call over the whole manual. Replaces the table when it answers; leaves it standing
// when it fails, so a 502 does not empty a table somebody was reading.
export async function findHardwareInteractions(
  docId: string,
  file: Blob,
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  items: PartItem[],
  model: string,
  t: (k: string) => string,
) {
  if (store.get(docId).busy) return;
  if (!model || !steps.some((s) => (subSteps[s.category] ?? []).length)) {
    store.patch(docId, { error: t('notice.hardwareTable.needSteps') });
    return;
  }
  // The ontology before the prompt, and waited for: until it lands every category reads as a
  // component, and the hardware list would be empty. See findStepHoles, which says it at length.
  await ensureCategories();
  const halted = haltGate(docId);
  const startedAt = Date.now();
  store.patch(docId, { busy: true, error: null, startedAt, elapsed: null });

  let task: PDFDocumentLoadingTask | null = null;
  try {
    task = loadPdf(await file.arrayBuffer());
    const doc = await task.promise;
    const images: string[] = [];
    const pages: number[] = [];
    for (const step of steps) {
      if (!(subSteps[step.category] ?? []).length) continue;
      for (const page of step.pages) {
        if (halted() || images.length >= MAX_IMAGES) break;
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const image = await renderPdfPageBand(doc, page, band.top, band.bottom, WIDTH);
        if (image) {
          images.push(image);
          pages.push(page);
        }
      }
    }
    if (halted()) return;

    const { data } = await axios.post<{
      interactions?: HardwareInteraction[]; alone?: HardwareAlone[]; reasoning?: string; raw?: string;
    }>('/api/notice/hardware-interactions', {
      model,
      images,
      pages,
      prompt: buildInteractionsPrompt(interactionsContextFor(steps, subSteps, items)),
    });
    if (halted()) return;

    // THE IDS COME BACK ONE HIGHER THAN THEY ARE STORED (see shownId), and are put back as they
    // land, as every other call here does.
    const shift = (id: string) => storedId(id, items.length);
    const holesOf = (list?: TableHole[]) =>
      (list ?? []).map((h) => ({
        ...h, part: shift(h.part), for: shift(h.for), crosses: h.crosses ? shift(h.crosses) : null,
        aligned: h.aligned ? shift(h.aligned) : null,
      }));
    const was = store.get(docId);
    // AND THE HOLES ALREADY PLACED, by the ids they were placed under (see keepHoleIds).
    const placed = placedHoles(docId);
    const asked = (data.interactions ?? []).map((one) => ({
      ...one,
      counts: Object.fromEntries(Object.entries(one.counts ?? {}).map(([id, n]) => [shift(id), n])),
      side: one.side ?? null,
      hardware: one.hardware.map(shift),
      covers: (one.covers ?? []).map(shift),
      washers: Object.fromEntries(Object.entries(one.washers ?? {}).map(([w, on]) => [shift(w), shift(on)])),
      components: one.components.map(shift),
      through: (one.through ?? []).map(shift),
      holes: holesOf(one.holes),
    }));
    const askedAlone = (data.alone ?? []).map((one) => ({
      ...one,
      count: one.count ?? null,
      side: one.side ?? null,
      hardware: shift(one.hardware),
      components: one.components.map(shift),
      through: (one.through ?? []).map(shift),
      holes: holesOf(one.holes),
    }));
    // BOTH LISTS AT ONCE: their holes are numbered as one (see keepHoleIds).
    const kept = keepHoleIds<HardwareInteraction | HardwareAlone>([...asked, ...askedAlone],
      [...(was.interactions ?? []), ...(was.alone ?? [])], placed);
    const interactions = kept.slice(0, asked.length) as HardwareInteraction[];
    const alone = kept.slice(asked.length) as HardwareAlone[];
    const elapsed = Math.round((Date.now() - startedAt) / 1000);
    const reasoning = data.reasoning || '';
    const raw = data.raw || '';
    const basis = stepsBasis(steps, subSteps);
    writeState(docId, key(docId), { interactions, alone, reasoning, raw, elapsed, basis });
    store.patch(docId, { interactions, alone, asked: true, reasoning, raw, basis });
  } catch (e) {
    const msg =
      (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
      (e instanceof Error && e.message) ||
      t('notice.err.generic');
    store.patch(docId, { error: msg });
  } finally {
    store.patch(docId, {
      busy: false,
      startedAt: null,
      elapsed: Math.round((Date.now() - startedAt) / 1000),
    });
    void task?.destroy();
  }
}

// THE ROWS ONE PART IS IN, as its 3D build is told them: the OTHER hardware in each, the
// component parts it goes through, and how they engage — named, in English, because this is
// prompt copy. What orients the build (see openai_blender.interactions_block).
export function interactionsOfPart(
  interactions: HardwareInteraction[], alone: HardwareAlone[], partAt: number, items: PartItem[],
): WrittenInteraction[] {
  const me = String(partAt);
  const name = (id: string) => {
    const p = items[Number(id)];
    return p ? withLetter(namedIn(p, 'en') || `#${id}`, p.letter) : `#${id}`;
  };
  // AND WHETHER IT IS A COVER — of the others' hole in a row, or straight on a part (a felt pad).
  // Which face touches is decided when it is built (see openai_blender._COVER_RULE).
  return [
    ...interactions
      .filter((one) => one.hardware.includes(me))
      .map((one) => ({
        with: one.hardware.filter((id) => id !== me).map(name),
        through: one.components.map(name),
        how: one.descriptionEn || one.descriptionFr,
        ...(one.covers.includes(me) ? { cover: true } : {}),
      })),
    ...alone
      .filter((one) => one.covers && one.hardware === me)
      .map((one) => ({
        with: [], through: one.components.map(name), how: one.descriptionEn || one.descriptionFr, cover: true,
      })),
  ];
}
